<template>
  <ion-toolbar>
    <ion-buttons slot="start"  v-if="$store.state.pages.auth.back.length || routeName == 'profile_preferred_state'">
      <ion-button @click="goBack">
        <ion-icon name="arrow-back"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Preferred states</ion-title>
    <ion-buttons v-if="routeName != 'profile_preferred_state'" slot="end">
      <ion-button @click="skip" class="pill next-pill-button">
                <span>Skip</span>
            </ion-button>
    </ion-buttons>
  </ion-toolbar>

  <div class="main_wrapper ion-padding">
    <div class="container">
      <div class="auth-header">
        <div class="auth-heading">
          <h1>Select Your Preferred States</h1>
          <p>Choose the states where you'd like to work</p>
        </div>
      </div>

      <div class="states-container">
        <div v-if="loading" class="states-skeleton">
          <el-skeleton :rows="8" animated>
            <template #template>
              <div 
                v-for="n in 8" 
                :key="n"
                class="skeleton-state-item"
              >
                <el-skeleton-item variant="rect" style="width: 20px; height: 20px; border-radius: 4px;" />
                <el-skeleton-item variant="text" style="width: 150px; height: 16px; margin-left: 8px;" />
              </div>
            </template>
          </el-skeleton>
        </div>
        <div v-else-if="states.length > 0" class="states-list">
          <el-checkbox-group v-model="selectedStates">
            <div 
              v-for="state in states" 
              :key="getStateId(state)"
              class="state-item"
            >
              <el-checkbox 
                :label="getStateId(state)"
              >
                {{ state.name }}
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>
        <div v-else class="no-states">
          <p>No states available</p>
        </div>
      </div>
      
      <el-form-item class="button_submit mb0">
        <el-button 
        class="submit w-100" 
          type="blue" 
          @click="submitPreferredStates"
          :loading="submitting"
          :disabled="isSubmitDisabled"
        >
          <span v-if="routeName == 'profile_preferred_state'">Save</span>
          <span v-else>Continue</span>
        </el-button>
      </el-form-item>


    </div>
  </div>

  <modal-user-info :modalName="'userInfoModal'" :options="userInfoModal"></modal-user-info>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  inject,
  getCurrentInstance,
} from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import {
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
} from "@ionic/vue";
import {
  ElCheckbox,
  ElCheckboxGroup,
  ElButton,
  ElMessage,
  ElFormItem,
  ElSkeleton,
  ElSkeletonItem,
} from "@/components/Vendor/Element";
import { UserInfoModal } from "@/modals/modals";
import { ElMessageBox } from "@/components/Vendor/Element";


export default {
  components: {
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    ElCheckbox,
    ElCheckboxGroup,
    ElButton,
    ElFormItem,
    ElSkeleton,
    ElSkeletonItem,
    "modal-user-info": UserInfoModal,
  },
  setup() {
    const $store = useStore();
    const $router = useRouter();
    const $route = useRoute();
    const $modals = inject("$modals");
    const instance = getCurrentInstance();
    const routeName = $route.name;
    const loading = ref(false);
    const submitting = ref(false);
    const states = ref([]);
    const selectedStates = ref([]);
    const initialSelectedStates = ref([]);

    const user = $store.getters.GET_USER_DATA;
    console.log("🚀 ~ user:", user.address.state)

    // Get state ID for checkbox value (prioritizes alphaCode)
    const getStateId = (state) => {
      return state.alphaCode || state.alphaCode || state.id || state.name;
    };

    // Get alphaCode specifically for payload
    const getStateAlphaCode = (state) => {
      if (typeof state === 'string') {
        return state; // Already an alphaCode string
      }
      return state?.alphaCode || state?.alphaCode || state?.id || state?.name;
    };

    const goBack = async() => {
      if(routeName == 'profile_preferred_state') {
        $router.push({
          name: "profile",
        });
      } else {
        const back = await $store.dispatch("getAuthPageBack");
        $router.push({
          name: back,
        });
      }
    };

    const userInfoModal = ref({
      name: "userInfo",
      user: {},
    });

    const userInfoModalOpen = (user) => {
      if (user.locum != null) {
        userInfoModal.value.user = user.locum;
        $modals.open("userInfoModal", {
          user: user.locum,
        });
      } else {
        ElMessage.error(`Invited to ${instance?.appContext.config.globalProperties.$filters.appName()}`);
      }
    };

    const loadStates = async () => {
      loading.value = true;
      try {
        const response = await $store.dispatch("GET_STATES");
        states.value = Array.isArray(response) ? response : [];
        
        // Initialize selected states from user data
        let initialStates = [];
        const userPreferredStates = user.settings?.preferredStates || user.preferredStates || user.preferred_states;
        
        if (userPreferredStates && Array.isArray(userPreferredStates)) {
          initialStates = userPreferredStates.map(state => {
            if (typeof state === 'object') {
              return getStateAlphaCode(state);
            }
            // If string, try to find matching state
            const matchedState = states.value.find(s => 
              (s.alphaCode || s.alphaCode) === state || s.id === state || s.name === state
            );
            return matchedState ? getStateAlphaCode(matchedState) : state;
          });
        }
        
        // Set initialSelectedStates to only saved preferred states (for change detection)
        initialSelectedStates.value = [...initialStates];
        
        // Check if user.address.state exists and pre-select it
        // Add it to selectedStates but NOT to initialSelectedStates (so button is enabled)
        let selectedStatesList = [...initialStates];
        if (user.address?.state && routeName === "auth_preferred_state") {
          const addressStateCode = user.address.state; // e.g., "VIC"
          // Find matching state in loaded states
          const matchedState = states.value.find(s => 
            (s.alphaCode || s.alphaCode) === addressStateCode || 
            s.id === addressStateCode || 
            s.name === addressStateCode
          );
          
          if (matchedState) {
            const stateId = getStateId(matchedState);
            // Add to selectedStates if not already present
            if (!selectedStatesList.includes(stateId)) {
              selectedStatesList.push(stateId);
            }
          }
        }
        
        selectedStates.value = selectedStatesList;
      } catch (error) {
        console.error("Error loading states:", error);
        ElMessage.error("Failed to load states");
      } finally {
        loading.value = false;
      }
    };

    // Check if form has changes
    const hasChanges = computed(() => {
      if (selectedStates.value.length !== initialSelectedStates.value.length) {
        return true;
      }
      // Check if arrays have different values
      const sortedCurrent = [...selectedStates.value].sort().join(',');
      const sortedInitial = [...initialSelectedStates.value].sort().join(',');
      return sortedCurrent !== sortedInitial;
    });

    // Submit button disabled state
    const isSubmitDisabled = computed(() => {
      // Disable if submitting
      if (submitting.value) {
        return true;
      }
      // Disable if no states selected (validation: must have at least one)
      if (selectedStates.value.length === 0) {
        return true;
      }
      // Disable if no changes made
      if (!hasChanges.value) {
        return true;
      }
      return false;
    });

    const skip = () => {
            (async () => {
                try {
                    await ElMessageBox.confirm(
                        ``,
                        "Do you want to skip? You will be unverified",
                        {
                            distinguishCancelAndClose: true,
                            showConfirmButton: true,
                            confirmButtonText: "Skip",
                            cancelButtonText: "Cancel",
                            confirmButtonClass: "el-button--green",
                            cancelButtonClass: "el-button--grey",
                            showClose: true,
                        }
                    );
                } catch {
                    return;
                }
                try {
                    $store.dispatch('addAuthPageBack', $route.name);
                    $router.push({
                        name: 'auth_availability_shift_type',
                    })
                } catch (error) {
                    console.log(error);
                }
            })();
          
        }

    const submitPreferredStates = async () => {
      // Validation: must have at least one state selected
      if (selectedStates.value.length === 0) {
        ElMessage.warning("Please select at least one preferred state");
        return;
      }

      submitting.value = true;
      try {
        // Convert selected state IDs to alphaCodes for payload
        const alphaCodes = selectedStates.value.map(stateId => {
          const state = states.value.find(s => getStateId(s) === stateId);
          return getStateAlphaCode(state || stateId);
        });

        const payload = {
          preferred_states: alphaCodes  // Array of alphaCodes: ["SA", "TAS"]
        };
        
        await $store.dispatch("SAVE_USER_DATA", payload);
        ElMessage.success("Preferred states saved successfully");
        
        // Update initial states to current selection
        initialSelectedStates.value = [...selectedStates.value];
        
        // Navigation logic (route-specific)
        if (routeName === "auth_preferred_state") {
          $router.push({ name: "auth_availability_shift_type" });
        } else {
          // Stay on page
          return;
        }
      } catch (error) {
        console.error("Error saving preferred states:", error);
        ElMessage.error("Failed to save preferred states");
      } finally {
        submitting.value = false;
      }
    };

    onMounted(async () => {
      await loadStates();
    });

    return {
      loading,
      submitting,
      states,
      selectedStates,
      getStateId,
      submitPreferredStates,
      isSubmitDisabled,
      goBack,
      userInfoModal,
      userInfoModalOpen,
      user,
      skip,
      routeName,
    };
  },
};
</script>

<style scoped lang="scss">
.main_wrapper {
  min-height: 100vh;
  position: relative;
}

.container {
  max-width: 600px;
  margin: 0 auto;
  padding: 24px 16px;
}

.auth-header {
  margin-bottom: 32px;
  text-align: center;

  .auth-heading {
    h1 {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 8px;
      color: var(--ion-text-color);
    }

    p {
      font-size: 14px;
      color: var(--ion-color-medium);
      margin: 0;
    }
  }
}

.states-container {
  margin-top: 24px;
  margin-bottom: 24px;
}

.states-list {
  .state-item {
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 8px;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--ion-color-light);
    }
  }
}

.states-skeleton {
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 16px 0;
}

.skeleton-state-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.no-states {
  text-align: center;
  padding: 40px 20px;
  color: var(--ion-color-medium);

  p {
    font-size: 16px;
    margin: 0;
  }
}


</style>

